import { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import {
  ArrowLeft, MapPin, Phone, Clock, ChevronDown, ChevronLeft,
  ChevronRight, Printer, Package, User, CreditCard, AlertCircle,
  RefreshCw, Hash, Calendar, Loader2
} from 'lucide-react';
import { ordersService, Order, OrderStatus } from '../../services/orders.service';
import { notificationsService } from '../../services/notifications.service';
import { OrderReceipt } from './OrderReceipt';
import { PaymentModal } from '../tables/PaymentModal';
import { useCurrency } from '../../hooks/useCurrency';
import { useOrders } from '../../hooks/useOrders';
import { useAuth } from '../../contexts/AuthContext';
import { hasEffectivePermission } from '../../utils/rbac';
import { toast } from 'sonner';

interface OrderDetailsPageProps {
  orderId: string;
  onNavigate?: (page: string) => void;
  onBack?: () => void;
  backLabel?: string;
  onOrderChange?: (orderId: string) => void;
}

const STATUS_CONFIG: Record<OrderStatus, { label: string; bgColor: string; dotColor: string }> = {
  PENDING:   { label: 'New Order',    bgColor: 'bg-orange-600', dotColor: 'bg-orange-400' },
  CONFIRMED: { label: 'Confirmed',    bgColor: 'bg-blue-600',   dotColor: 'bg-blue-400' },
  PREPARING: { label: 'Preparing',    bgColor: 'bg-purple-600', dotColor: 'bg-purple-400' },
  READY:     { label: 'Ready',        bgColor: 'bg-indigo-600', dotColor: 'bg-indigo-400' },
  DELIVERED: { label: 'Delivered',    bgColor: 'bg-green-600',  dotColor: 'bg-green-400' },
  CLOSED:    { label: 'Closed',       bgColor: 'bg-gray-600',   dotColor: 'bg-gray-400' },
  CANCELLED: { label: 'Cancelled',    bgColor: 'bg-red-600',    dotColor: 'bg-red-400' },
  REJECTED:  { label: 'Rejected',     bgColor: 'bg-red-600',    dotColor: 'bg-red-400' },
  REFUNDED:  { label: 'Refunded',     bgColor: 'bg-yellow-600', dotColor: 'bg-yellow-400' },
};

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'PENDING',   label: 'New Order' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PREPARING', label: 'Preparing' },
  { value: 'READY',     label: 'Ready' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CLOSED',    label: 'Closed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED',  label: 'Rejected' },
  { value: 'REFUNDED',  label: 'Refunded' },
];

export function OrderDetailsPage({ orderId, onNavigate, onBack, backLabel = 'Orders', onOrderChange }: OrderDetailsPageProps) {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const { orders: ordersData, total: ordersTotal, stats } = useOrders({ page: 1, limit: 50 });
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );

  const canViewOrderDetails = hasEffectivePermission(user, 'view_order_details');
  const isChef = user?.role === 'chef';
  
  // Chef restricted statuses: cannot change to PENDING, DELIVERED, CLOSED, or REFUNDED
  const chefRestrictedStatuses = ['PENDING', 'DELIVERED', 'CLOSED', 'REFUNDED'];
  const canChefUpdateToStatus = (status: OrderStatus): boolean => {
    return !chefRestrictedStatuses.includes(status);
  };

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  const approvalRequiredStatuses: OrderStatus[] = ['REJECTED', 'CANCELLED', 'REFUNDED'];
  const statusActionLabel: Record<OrderStatus, string> = {
    PENDING: 'Pending',
    CONFIRMED: 'Confirmed',
    PREPARING: 'Preparing',
    READY: 'Ready',
    DELIVERED: 'Delivered',
    CLOSED: 'Closed',
    CANCELLED: 'Cancelled',
    REJECTED: 'Rejected',
    REFUNDED: 'Refunded',
  };

  const [order, setOrder] = useState<Order | null>(null);
  const [allOrderIds, setAllOrderIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<OrderStatus | null>(null);
  const [showStatusChangeDialog, setShowStatusChangeDialog] = useState(false);
  const [requestedStatus, setRequestedStatus] = useState<OrderStatus | null>(null);
  const [pendingApprovalStatus, setPendingApprovalStatus] = useState<OrderStatus | null>(null);
  const [pendingApprovalNotificationId, setPendingApprovalNotificationId] = useState<string | null>(null);
  const [sendingApprovalRequest, setSendingApprovalRequest] = useState(false);
  const [resolvingApproval, setResolvingApproval] = useState(false);

  // Fetch the specific order
  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ordersService.getOrderById(orderId);
      if (response.success && response.data) {
        setOrder(response.data);
        setCurrentStatus(response.data.status);
      } else {
        setError('Order not found.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load order details.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Fetch all order IDs for prev/next navigation
  const fetchAllOrderIds = useCallback(async () => {
    try {
      const response = await ordersService.getOrders({ limit: 200, sortBy: 'createdAt', sortOrder: 'desc' });
      if (response.success && response.data) {
        // Use real UUID id for navigation (matches what onOrderClick now passes)
        const ids = response.data.data.map((o: any) => o.id);
        setAllOrderIds(ids);
      }
    } catch {
      // non-critical, ignore
    }
  }, []);

  useEffect(() => {
    fetchOrder();
    fetchAllOrderIds();
  }, [fetchOrder, fetchAllOrderIds]);

  // Check if this order has a pending status-change approval in localStorage
  useEffect(() => {
    if (order?.id) {
      const pending = JSON.parse(localStorage.getItem('qrypt_pending_status_changes') || '{}');
      const legacyPending = JSON.parse(localStorage.getItem('qrypt_pending_rejections') || '{}');
      const orderPending = pending[order.id];

      if (orderPending?.requestedStatus) {
        setPendingApprovalStatus(orderPending.requestedStatus as OrderStatus);
        setPendingApprovalNotificationId(orderPending.notificationId || null);
      } else if (legacyPending[order.id]) {
        setPendingApprovalStatus('REJECTED');
        setPendingApprovalNotificationId(null);
      } else {
        setPendingApprovalStatus(null);
        setPendingApprovalNotificationId(null);
      }
    }
  }, [order?.id]);

  const clearPendingStatusChange = (orderIdToClear: string) => {
    const pending = JSON.parse(localStorage.getItem('qrypt_pending_status_changes') || '{}');
    delete pending[orderIdToClear];
    localStorage.setItem('qrypt_pending_status_changes', JSON.stringify(pending));

    const legacyPending = JSON.parse(localStorage.getItem('qrypt_pending_rejections') || '{}');
    delete legacyPending[orderIdToClear];
    localStorage.setItem('qrypt_pending_rejections', JSON.stringify(legacyPending));

    setPendingApprovalStatus(null);
    setPendingApprovalNotificationId(null);
  };

  const handleApproveFromOrderDetails = async () => {
    if (!order || !isAdminOrManager || !pendingApprovalNotificationId) return;

    setResolvingApproval(true);
    try {
      await notificationsService.approveStatusChangeRequest(pendingApprovalNotificationId);
      clearPendingStatusChange(order.id);
      await fetchOrder();
      toast.success('Status change approved and applied');
    } catch {
      toast.error('Failed to approve request. Please try again.');
    } finally {
      setResolvingApproval(false);
    }
  };

  const handleDenyFromOrderDetails = async () => {
    if (!order || !isAdminOrManager || !pendingApprovalNotificationId) return;

    setResolvingApproval(true);
    try {
      await notificationsService.denyStatusChangeRequest(pendingApprovalNotificationId);
      clearPendingStatusChange(order.id);
      toast.success('Status change request denied');
    } catch {
      toast.error('Failed to deny request. Please try again.');
    } finally {
      setResolvingApproval(false);
    }
  };

  // Prev/next navigation
  const currentIndex = allOrderIds.indexOf(orderId);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < allOrderIds.length - 1;

  const handlePrevious = () => {
    if (hasPrevious && onOrderChange) onOrderChange(allOrderIds[currentIndex - 1]);
  };
  const handleNext = () => {
    if (hasNext && onOrderChange) onOrderChange(allOrderIds[currentIndex + 1]);
  };

  // Handle sensitive status changes — admin/manager direct, others request approval
  const handleSensitiveStatusRequest = async () => {
    if (!order || !user || !requestedStatus) return;

    if (isAdminOrManager) {
      setShowStatusChangeDialog(false);
      await handleStatusChange(requestedStatus);
      return;
    }

    setSendingApprovalRequest(true);
    try {
      if (!approvalRequiredStatuses.includes(requestedStatus)) {
        toast.error('This status does not require approval');
        return;
      }

      const readableStatus = statusActionLabel[requestedStatus];
      const approvalResponse = await notificationsService.requestStatusChangeApproval({
        orderId: order.id,
        orderNumber: order.orderNumber || order.id,
        requestedStatus: requestedStatus as 'REJECTED' | 'CANCELLED' | 'REFUNDED',
      });

      const sentTo = approvalResponse.data?.sentTo || 0;
      if (!approvalResponse.success || sentTo === 0) {
        toast.error('No managers or admins found to approve this request');
        return;
      }

      const existing = JSON.parse(localStorage.getItem('qrypt_pending_status_changes') || '{}');
      existing[order.id] = {
        orderNumber: order.orderNumber || order.id,
        requestedStatus,
        requestedByName: user.name,
        requestedAt: new Date().toISOString(),
      };
      localStorage.setItem('qrypt_pending_status_changes', JSON.stringify(existing));

      setPendingApprovalStatus(requestedStatus);
      setShowStatusChangeDialog(false);
      toast.success(`Approval request sent to ${sentTo} manager${sentTo !== 1 ? 's/admins' : '/admin'}`);
    } catch {
      toast.error('Failed to send approval request. Please try again.');
    } finally {
      setSendingApprovalRequest(false);
    }
  };

  // Status change
  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (!order) return;
    setStatusUpdating(true);
    setStatusDropdownOpen(false);
    try {
      const response = await ordersService.updateOrderStatus(order.id, newStatus);
      if (response.success && response.data) {
        setCurrentStatus(newStatus);
        setOrder((prev) => prev ? { ...prev, status: newStatus } : prev);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!order) return;

    if (['CLOSED', 'CANCELLED', 'REJECTED', 'REFUNDED'].includes(order.status)) {
      toast.error('This order is not available for payment');
      return;
    }

    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async (paymentData: {
    method: 'CASH' | 'MPESA' | 'CARD';
    amount: number;
    discount: number;
  }) => {
    if (!order) return;

    setProcessingPayment(true);

    try {
      const paymentNote = `Payment: ${paymentData.method} - Amount: ${formatCurrency(paymentData.amount)}${paymentData.discount > 0 ? ` (${paymentData.discount}% discount)` : ''}`;
      const existingNotes = order.notes?.trim();

      const response = await ordersService.updateOrder(order.id, {
        status: 'CLOSED',
        notes: existingNotes ? `${existingNotes}\n${paymentNote}` : paymentNote,
      });

      if (response.success && response.data) {
        setOrder(response.data);
        setCurrentStatus('CLOSED');
        setShowPaymentModal(false);
        toast.success('Payment completed successfully');
      }
    } catch (error) {
      console.error('Failed to process payment:', error);
      toast.error('Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  // ── Order details access restriction ─────────────────────────────────────
  if (!canViewOrderDetails) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(!sidebarOpen)} 
          activePage="orders" 
          onNavigate={onNavigate}
          ordersCount={ordersTotal || stats?.total || ordersData.length}
        />
        <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
          <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} onOrderClick={onOrderChange} />
          <main className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">You do not have permission to view order details. Contact an administrator if you need access.</p>
              <button onClick={onBack} className="px-4 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                ← Back to {backLabel}
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(!sidebarOpen)} 
          activePage="orders" 
          onNavigate={onNavigate}
          ordersCount={ordersTotal || stats?.total || ordersData.length}
        />
        <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
          <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} onOrderClick={onOrderChange} />
          <main className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Loading order details…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error || !order) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(!sidebarOpen)} 
          activePage="orders" 
          onNavigate={onNavigate}
          ordersCount={ordersTotal || stats?.total || ordersData.length}
        />
        <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
          <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} onOrderClick={onOrderChange} />
          <main className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Order Not Found</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{error || 'The order could not be loaded.'}</p>
              <div className="flex gap-3 justify-center">
                <button onClick={onBack} className="px-4 py-2 text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  ← Back to {backLabel}
                </button>
                <button onClick={fetchOrder} className="px-4 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2">
                  <RefreshCw className="w-3.5 h-3.5" /> Retry
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  const activeStatus = currentStatus || order.status;
  const statusCfg = STATUS_CONFIG[activeStatus] || STATUS_CONFIG.PENDING;
  const terminalStatuses: OrderStatus[] = ['CLOSED', 'CANCELLED', 'REJECTED', 'REFUNDED'];
  const nonPrintableStatuses: OrderStatus[] = ['CANCELLED', 'REJECTED', 'REFUNDED'];
  const isTerminalStatus = terminalStatuses.includes(activeStatus);
  const canPrintReceipt = !nonPrintableStatuses.includes(activeStatus);
  const canProcessPayment = !terminalStatuses.includes(activeStatus);
  const isOrderActionDisabled = !!pendingApprovalStatus;
  const isStatusDropdownDisabled = statusUpdating || !!pendingApprovalStatus;

  // Handle both flat (service interface) and nested (raw API) shapes
  const raw = order as any;

  const customerName =
    order.customerName ||
    (raw.customer ? `${raw.customer.firstName ?? ''} ${raw.customer.lastName ?? ''}`.trim() : '') ||
    'Unknown Customer';

  const customerEmail =
    order.customerEmail || raw.customer?.email || '—';

  const customerPhone =
    order.customerPhone || raw.customer?.phone || raw.customer?.phoneNumber || '—';

  const deliveryAddress = raw.deliveryAddress || '—';
  const notes = order.notes || raw.deliveryNotes || null;
  const paymentMethod = order.paymentMethod || raw.paymentMethod || 'N/A';

  // Check if this is a table order
  const isTableOrder = notes?.includes('Table:') || false;
  
  // Get waiter/server information
  const waiterName = raw.user 
    ? `${raw.user.firstName ?? ''} ${raw.user.lastName ?? ''}`.trim()
    : 'Unknown';
  const waiterEmail = raw.user?.email || '—';

  const safeNum = (v: any) => {
    const n = Number(v ?? 0);
    return isNaN(n) ? 0 : n;
  };

  const subtotal  = safeNum(order.subtotal || (order as any).totalAmount || order.total);
  const tax       = safeNum(order.tax);
  const discount  = safeNum(order.discount);
  const total     = safeNum(order.total || (order as any).totalAmount);
  const deliveryFee = safeNum((order as any).deliveryFee);

  const orderDate = order.orderDate
    ? new Date(order.orderDate).toLocaleString('en-US', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : new Date(order.createdAt).toLocaleString('en-US', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const items = (order.items || []).map((item: any) => ({
    ...item,
    // Resolve product name from nested product object if flat field is missing
    productName: item.productName || item.product?.name || item.name || 'Unknown Item',
    productSku:  item.productSku  || item.product?.sku  || item.sku  || '',
  }));

  // Build history timeline from status
  const statusHistory = [
    { label: 'Order Created',     done: true,                                               date: new Date(order.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) },
    { label: 'Order Confirmed',   done: ['CONFIRMED','PREPARING','READY','DELIVERED'].includes(activeStatus), date: '' },
    { label: 'Preparing',         done: ['PREPARING','READY','DELIVERED'].includes(activeStatus),             date: '' },
    { label: 'Ready for Pickup',  done: ['READY','DELIVERED'].includes(activeStatus),                         date: '' },
    { label: 'Delivered',         done: activeStatus === 'DELIVERED',                                         date: activeStatus === 'DELIVERED' && order.deliveryDate ? new Date(order.deliveryDate).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '' },
  ];

  if (activeStatus === 'CANCELLED') {
    statusHistory.push({ label: 'Cancelled', done: true, date: '' });
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="orders"
        onNavigate={onNavigate}
        ordersCount={ordersTotal || stats?.total || ordersData.length}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} onOrderClick={onOrderChange} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">

            {/* ── Page Header ── */}
            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <button onClick={onBack} className="p-2 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors">
                  <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    Order {order.orderNumber || order.id}
                  </h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <button onClick={onBack} className="text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium">
                      {backLabel}
                    </button>
                    <span className="text-gray-300 dark:text-gray-700 text-xs">/</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Order Details</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Pending status-change approval badge */}
                {pendingApprovalStatus && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg text-xs font-semibold text-amber-800 dark:text-amber-300">
                    <Clock className="w-3.5 h-3.5" />
                    Pending {statusActionLabel[pendingApprovalStatus]} Approval
                  </div>
                )}

                {pendingApprovalStatus && isAdminOrManager && pendingApprovalNotificationId && (
                  <>
                    <button
                      onClick={handleApproveFromOrderDetails}
                      disabled={resolvingApproval}
                      className="px-3 py-2 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resolvingApproval ? 'Processing...' : `Approve ${statusActionLabel[pendingApprovalStatus]}`}
                    </button>
                    <button
                      onClick={handleDenyFromOrderDetails}
                      disabled={resolvingApproval}
                      className="px-3 py-2 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Deny
                    </button>
                  </>
                )}

                {canProcessPayment && (
                  <button
                    onClick={handleProceedToPayment}
                    disabled={processingPayment || isOrderActionDisabled}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Proceed to Payment
                  </button>
                )}

                {canPrintReceipt && (
                  <button
                    onClick={() => setShowReceipt(true)}
                    disabled={isOrderActionDisabled}
                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Printer className="w-4 h-4" />
                    Print Receipt
                  </button>
                )}

                {/* Status Dropdown */}
                {isTerminalStatus ? (
                  <div className={`${statusCfg.bgColor} text-white px-4 py-2 rounded-lg flex items-center gap-2 text-xs font-semibold shadow-sm uppercase tracking-wide`}>
                    {statusCfg.label}
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      disabled={isStatusDropdownDisabled}
                      onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                      className={`${statusCfg.bgColor} text-white px-4 py-2 rounded-lg hover:opacity-90 transition-all flex items-center gap-2 text-xs font-semibold shadow-sm uppercase tracking-wide disabled:opacity-70 disabled:cursor-not-allowed`}
                    >
                      {statusUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {statusCfg.label}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {statusDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownOpen(false)} />
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                          {STATUS_OPTIONS.map((opt) => {
                            const isChefRestricted = isChef && chefRestrictedStatuses.includes(opt.value);
                            return (
                              <button
                                key={opt.value}
                                onClick={() => {
                                  if (isChefRestricted) {
                                    toast.error(`Chef cannot change order to "${opt.label}" status`);
                                    return;
                                  }
                                  if (approvalRequiredStatuses.includes(opt.value)) {
                                    setStatusDropdownOpen(false);
                                    setRequestedStatus(opt.value);
                                    setShowStatusChangeDialog(true);
                                    return;
                                  }
                                  handleStatusChange(opt.value);
                                }}
                                disabled={isChefRestricted}
                                className={`w-full text-left px-4 py-2.5 text-xs font-medium transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center gap-2 ${
                                  isChefRestricted
                                    ? 'text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-900 cursor-not-allowed opacity-60'
                                    : activeStatus === opt.value
                                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                                    : 'text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                              >
                                <span className={`w-2 h-2 rounded-full ${STATUS_CONFIG[opt.value].dotColor}`} />
                                <span className="flex-1">{opt.label}</span>
                                {isChefRestricted && <span className="ml-auto text-xs">🔒</span>}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Prev/Next */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={handlePrevious}
                    disabled={!hasPrevious}
                    className={`p-2 rounded-lg border transition-colors ${hasPrevious ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : 'border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-600 cursor-not-allowed'}`}
                    title="Previous order"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!hasNext}
                    className={`p-2 rounded-lg border transition-colors ${hasNext ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : 'border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-600 cursor-not-allowed'}`}
                    title="Next order"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* ── Main Grid ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* ── Left Column ── */}
              <div className="space-y-6">

                {/* Customer or Waiter Card - Show waiter for table orders, customer for delivery */}
                {isTableOrder ? (
                  // Waiter/Server Card for Table Orders
                  <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <div className="flex flex-col items-center text-center mb-6">
                      <div className="w-20 h-20 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center mb-3">
                        <User className="w-9 h-9 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">{waiterName}</h3>
                      <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Serving Waiter</span>
                    </div>

                    <div className="space-y-3">
                      {waiterEmail !== '—' && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <div className="w-7 h-7 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px]">✉</span>
                          </div>
                          <span className="truncate">{waiterEmail}</span>
                        </div>
                      )}
                    </div>

                    {notes && (
                      <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
                        <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 mb-1 uppercase tracking-wide">Order Note</p>
                        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">{notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Customer Card for Delivery Orders
                  <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <div className="flex flex-col items-center text-center mb-6">
                      <div className="w-20 h-20 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center mb-3">
                        <User className="w-9 h-9 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">{customerName}</h3>
                      <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Customer</span>
                    </div>

                    <div className="space-y-3">
                      {customerEmail !== '—' && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <div className="w-7 h-7 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px]">✉</span>
                          </div>
                          <span className="truncate">{customerEmail}</span>
                        </div>
                      )}
                      {customerPhone !== '—' && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <div className="w-7 h-7 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Phone className="w-3.5 h-3.5" />
                          </div>
                          <span>{customerPhone}</span>
                        </div>
                      )}
                      {deliveryAddress !== '—' && (
                        <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <div className="w-7 h-7 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                            <MapPin className="w-3.5 h-3.5" />
                          </div>
                          <span>{deliveryAddress}</span>
                        </div>
                      )}
                    </div>

                    {notes && (
                      <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
                        <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 mb-1 uppercase tracking-wide">Order Note</p>
                        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">{notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Order Meta */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4 text-sm">Order Info</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <Hash className="w-3.5 h-3.5" />
                        <span>Order Number</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">{order.orderNumber || order.id}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>Order Date</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white text-right max-w-[150px]">{orderDate}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <CreditCard className="w-3.5 h-3.5" />
                        <span>Payment</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">{paymentMethod}</span>
                    </div>
                    {order.branchName && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <Package className="w-3.5 h-3.5" />
                          <span>Branch</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">{order.branchName}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order History / Timeline */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4 text-sm">Order Timeline</h3>
                  <div className="space-y-4">
                    {statusHistory.map((step, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className={`w-3 h-3 rounded-full mt-0.5 flex-shrink-0 ring-2 ${
                          step.done
                            ? 'bg-indigo-600 dark:bg-indigo-500 ring-indigo-200 dark:ring-indigo-800'
                            : 'bg-gray-200 dark:bg-gray-700 ring-gray-100 dark:ring-gray-800'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-semibold ${step.done ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                            {step.label}
                          </div>
                          {step.date && (
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{step.date}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Right Column ── */}
              <div className="lg:col-span-2 space-y-6">

                {/* Items Table */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">Order Items</h3>
                    <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full">
                      {items.length} {items.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>

                  {items.length === 0 ? (
                    <div className="py-12 text-center">
                      <Package className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-400 dark:text-gray-500">No items in this order</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-800/60">
                            <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 dark:text-gray-300">Item</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-700 dark:text-gray-300">Qty</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-700 dark:text-gray-300">Unit Price</th>
                            <th className="text-right px-6 py-3 text-xs font-semibold text-gray-700 dark:text-gray-300">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, idx) => {
                            const itemPrice = safeNum(item.price);
                            const itemQty   = safeNum(item.quantity);
                            const itemTotal = safeNum(item.total) || itemPrice * itemQty;
                            return (
                              <tr key={item.id || idx} className="border-t border-gray-50 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <Package className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
                                    </div>
                                    <div>
                                      <div className="text-xs font-semibold text-gray-900 dark:text-white">{item.productName}</div>
                                      {item.productSku && (
                                        <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">SKU: {item.productSku}</div>
                                      )}
                                      {item.notes && (
                                        <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 italic">{item.notes}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <span className="text-xs font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full">
                                    ×{itemQty}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-right text-xs font-medium text-gray-700 dark:text-gray-300">
                                  {formatCurrency(itemPrice)}
                                </td>
                                <td className="px-6 py-4 text-right text-xs font-semibold text-gray-900 dark:text-white">
                                  {formatCurrency(itemTotal)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Order Totals */}
                  <div className="border-t border-gray-100 dark:border-gray-800 px-6 py-4">
                    <div className="max-w-xs ml-auto space-y-2">
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Subtotal</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(subtotal)}</span>
                      </div>
                      {tax > 0 && (
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>Tax</span>
                          <span className="font-medium text-gray-900 dark:text-white">+{formatCurrency(tax)}</span>
                        </div>
                      )}
                      {deliveryFee > 0 && (
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>Delivery Fee</span>
                          <span className="font-medium text-gray-900 dark:text-white">+{formatCurrency(deliveryFee)}</span>
                        </div>
                      )}
                      {discount > 0 && (
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>Discount</span>
                          <span className="font-medium text-green-600 dark:text-green-400">-{formatCurrency(discount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-bold text-gray-900 dark:text-white border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                        <span>Total</span>
                        <span className="text-indigo-600 dark:text-indigo-400">{formatCurrency(total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4 text-sm">Delivery Details</h3>

                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 mb-5">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">🚚</span>
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">Delivery Status</div>
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          activeStatus === 'DELIVERED' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                          activeStatus === 'CANCELLED' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                          'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dotColor}`} />
                          {statusCfg.label}
                        </div>
                      </div>
                      {order.deliveryDate && (
                        <div className="text-right">
                          <div className="text-[10px] text-gray-400 dark:text-gray-500">Delivered on</div>
                          <div className="text-xs font-semibold text-gray-900 dark:text-white">
                            {new Date(order.deliveryDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-indigo-600 dark:bg-indigo-500 rounded-full flex items-center justify-center">
                          <Phone className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wide">Customer Phone</div>
                      </div>
                      <div className="text-xs font-semibold text-gray-900 dark:text-white">{customerPhone}</div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-indigo-600 dark:bg-indigo-500 rounded-full flex items-center justify-center">
                          <Clock className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wide">Order Time</div>
                      </div>
                      <div className="text-xs font-semibold text-gray-900 dark:text-white">
                        {new Date(order.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>

                    {deliveryAddress !== '—' && (
                      <div className="col-span-2 bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-white" />
                          </div>
                          <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wide">Delivery Address</div>
                        </div>
                        <div className="text-xs font-semibold text-gray-900 dark:text-white">{deliveryAddress}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ── Print Receipt Modal ── */}
      {showReceipt && (
        <OrderReceipt
          orderId={order.orderNumber || order.id}
          orderDate={orderDate}
          customerName={customerName}
          customerAddress={deliveryAddress !== '—' ? deliveryAddress : ''}
          customerPhone={customerPhone !== '—' ? customerPhone : ''}
          customerEmail={customerEmail !== '—' ? customerEmail : ''}
          items={items.map((item) => ({
            id: item.id || String(Math.random()),
            name: item.productName,
            quantity: safeNum(item.quantity),
            price: safeNum(item.price),
            total: safeNum(item.total) || safeNum(item.price) * safeNum(item.quantity),
          }))}
          subtotal={subtotal}
          tax={tax}
          deliveryFee={deliveryFee}
          total={total}
          paymentMethod={paymentMethod}
          onClose={() => setShowReceipt(false)}
        />
      )}

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        orderTotal={total}
        onPaymentComplete={handlePaymentComplete}
      />

      {/* ── Sensitive Status Confirmation / Approval Request Dialog ── */}
      {showStatusChangeDialog && order && requestedStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {isAdminOrManager ? `${statusActionLabel[requestedStatus]} Order` : `Request Order ${statusActionLabel[requestedStatus]}`}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Order {order.orderNumber || order.id}</p>
              </div>
            </div>

            {isAdminOrManager ? (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to mark this order as {statusActionLabel[requestedStatus]}? This action will change the order status and cannot be undone.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowStatusChangeDialog(false)}
                    className="px-4 py-2 text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSensitiveStatusRequest}
                    disabled={statusUpdating}
                    className="px-4 py-2 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {statusUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Confirm {statusActionLabel[requestedStatus]}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 mb-4">
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    <span className="font-semibold">Approval required:</span> Setting an order to {statusActionLabel[requestedStatus]} requires manager or admin approval. An approval request will be sent to all available managers and admins.
                  </p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Your request will appear on the notification dashboard of all managers and admins. The order status will only change once it is approved.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowStatusChangeDialog(false)}
                    className="px-4 py-2 text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSensitiveStatusRequest}
                    disabled={sendingApprovalRequest}
                    className="px-4 py-2 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {sendingApprovalRequest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Send Approval Request
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


